import { useCallback, useState, useTransition } from "react";
import { useAppSelector, useAppDispatch } from "store/hooks";
import { setPolicyNumber } from "store/application/applicationSlice";
import { transformDTOSubmissionData } from "mappers/transformDTOSubmissionData";
import { transformMLTASubmissionData } from "mappers/transformMLTASubmissionData";
import { submit, submitGIWLApplication, submitMLTAApplication } from "services/onlineApplicationService";
import { pushGTMEvent } from "utils/gtmEvent";
import { POLICY_DECISION_CONFIGS } from "constants/gaEvents";
import { ROUTES } from "routes/constants";
import { useProductContext } from "context/ProductContext";
import { transformGIWLSubmissionData } from "mappers/transformGIWLSubmissionData";
import { markPolicyEventFiredInSession } from "utils/abandonmentTracking";

type PolicyStatus = "approved" | "declined" | "alreadyused" | "refertounderwriter" | "incomplete" | "exception";

interface SubmissionResult {
    success: boolean;
    route: string;
    policyStatus?: PolicyStatus;
}

const approvedUrl = ROUTES.DECISION.SUCCESS;
const declinedUrl = ROUTES.DECISION.DENIED;
const welcomeBackUrl = ROUTES.WELCOME_BACK;
const needsACloserLookUrl = ROUTES.DECISION.NEEDS_CLOSER_LOOK;
const coverageLimitExceededUrl = ROUTES.DECISION.COVERAGE_LIMIT_EXCEEDED;
const unableToFinishUrl = ROUTES.DECISION.UNABLE_TO_FINISH;
const somethingWentWrongUrl = ROUTES.SOMETHING_WENT_WRONG;

function trackPolicyDecision(policyStatus: PolicyStatus, isResumedApplication: boolean): void {
    const cohort = isResumedApplication ? "resumed" : "regular";
    pushGTMEvent(POLICY_DECISION_CONFIGS[policyStatus][cohort]);
}

/**
 * Determines the navigation route based on policy status
 */
function toOutcomeUrlFromPolicyStatus(policyStatus: PolicyStatus): string {
    switch (policyStatus) {
        case "approved":
            return approvedUrl;
        case "declined":
            return declinedUrl;
        case "alreadyused":
            return welcomeBackUrl;
        case "incomplete":
            return welcomeBackUrl;
        default:
            return needsACloserLookUrl;
    }
}

/**
 * Determines the navigation route based on policy status for GIWL applications.
 * GIWL outcomes differ from DTO/MLTA: declined maps to the coverage-limit-exceeded
 * page, incomplete maps to the unable-to-finish page, and exception maps to
 * the something-went-wrong page.
 */
function toGIWLOutcomeUrlFromPolicyStatus(policyStatus: PolicyStatus): string {
    switch (policyStatus) {
        case "approved":
            return approvedUrl;
        case "declined":
            return coverageLimitExceededUrl;
        case "incomplete":
            return unableToFinishUrl;
        case "exception":
            return somethingWentWrongUrl;
        default:
            return needsACloserLookUrl;
    }
}

/**
 * Hook for submitting applications (DTO and MLTA)
 * Transforms Redux state data and submits to the appropriate API endpoint
 */
export const useSubmission = () => {
    // ========== Hooks ==========
    const { isMLTA, isGIWL } = useProductContext();
    const dispatch = useAppDispatch();
    const applicationState = useAppSelector((state) => state.application);

    // ========== State ==========
    const [isPending, startTransition] = useTransition();
    const [submissionError, setSubmissionError] = useState<string | null>(null);

    // ========== Event Handlers ==========
    const submitApplication = useCallback((): Promise<SubmissionResult> => {
        setSubmissionError(null);

        return new Promise<SubmissionResult>((resolve) => {
            startTransition(async () => {
                try {
                    // Transform and submit based on product type
                    let response;
                    if (isMLTA) response = await submitMLTAApplication(transformMLTASubmissionData(applicationState));
                    else if (isGIWL) {
                        const giwlResponse = await submitGIWLApplication(transformGIWLSubmissionData(applicationState));
                        // GIWL's policy number is minted by the backend and only available here;
                        // persist it so the success page can display it (DTO/MLTA set it earlier).
                        if (giwlResponse.policyNumber) dispatch(setPolicyNumber(giwlResponse.policyNumber));
                        response = giwlResponse;
                    } else response = await submit(transformDTOSubmissionData(applicationState));

                    const policyStatus = response.policyStatus;

                    trackPolicyDecision(policyStatus, applicationState.isResumedApplication);
                    markPolicyEventFiredInSession();

                    // Determine the outcome URL based on policy status
                    const outcomeUrl = isGIWL
                        ? toGIWLOutcomeUrlFromPolicyStatus(policyStatus)
                        : toOutcomeUrlFromPolicyStatus(policyStatus);

                    resolve({
                        success: true,
                        route: outcomeUrl,
                        policyStatus,
                    });
                } catch (error) {
                    console.error("Submission error:", error);
                    const errorMessage = error instanceof Error ? error.message : "Failed to submit application";
                    setSubmissionError(errorMessage);

                    resolve({
                        success: false,
                        route: ROUTES.SOMETHING_WENT_WRONG,
                    });
                }
            });
        });
    }, [applicationState, isMLTA, isGIWL, startTransition, dispatch]);

    return {
        submitApplication,
        isSubmitting: isPending,
        submissionError,
    };
};
